/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import { ServiceContext } from "@msx/platform-services";
import { fetchSegments, fetchSubSegments, fetchConfigurableTilesList } from "../../../../../utils/segmentApiUtils";

/**
 * useSegmentSelection — Manages segment/subsegment dropdown state and data fetching.
 *
 * Extracted from V2_Tile.tsx (PR #6, Phase 1).
 * Zero logic changes — pure cut-paste extraction.
 *
 * useState moved: segmentOptions, subSegmentOptions, segmentFinal, subSegmentFinal,
 *   segmentDropdownVisible, pendingSegmentState, irisUtterance, appName, tileName
 * Functions moved: getAllSegments, getAllSubSegmentFromSegment, passSegmentDataToIris,
 *   fetchDetails, hideSegmentDropdown, resetRedirectState, handleFormAndSegment, openWebFormFinal
 * Effects moved: pending segment webform effect, fetchDetails on mount
 */

interface UseSegmentSelectionParams {
  setShowBot: (val: boolean) => void;
  enableWwicCopilot: boolean;
  setIrisBotIconClicked: (val: boolean) => void;
  finalTileObject: any;
  // Cross-hook deps (workspace)
  dataServiceWorkspace: any[];
  errorData: any[];
  setErrorData: (val: any) => void;
  // PURL bridge state (owned by parent, passed to both hooks)
  isPUrlSegmentTile: boolean;
  setIsPUrlSegmentTile: (val: boolean) => void;
  urlTileData: any;
  setUrlTileData: (val: any) => void;
  purlProcessed: React.MutableRefObject<boolean>;
  // UI callbacks defined in parent
  checksIris: () => void;
  handleChatBotIconClick: (...args: any[]) => void;
  openWebForm: (...args: any[]) => void;
  setInfoPanelZIndex: (val: string) => void;
  // Workspace functions (from useWorkspaceTiles)
  fetchUserWorkspacesAndDefaultTiles: () => Promise<any>;
  validateResponse: (...args: any[]) => any;
  setDataServiceWorkspace: (val: any) => void;
  handlePanelOpening: (...args: any[]) => any;
  // Segment redirect state
  isRedirectFromAnotherForm: boolean;
  setIsRedirectFromAnotherForm: (val: boolean) => void;
  isopenSegment: boolean;
  SetIsopenSegment: (val: boolean) => void;
  userSelectedOption: string;
  setUserSelectedOption: (val: string) => void;
  setTileProfileResponse: (val: any) => void;
  setIsDropdownModalVisible: (val: boolean) => void;
  irisAppName: string;
  selectedTileId: any;
  setSelectedTileId: (val: any) => void;
  tileSelected: any;
  setTileSelected: (val: any) => void;
  setOpenPanelId: (val: any) => void;
  setDialogTitle: (val: string) => void;
  // Form/Iris functions from parent
  checkIrisService: (...args: any[]) => void;
  checkTileFormStatus: (card: any) => any;
  webFormNames: any[];
  isTileHasForm: boolean;
  setIsTileHasForm: (val: boolean) => void;
  reduxDispatch: any;
  copilotAccessMap: Record<string, { hasAccess: boolean; enableTileForm: boolean; enableSelfHelp: boolean }>;
}

export const useSegmentSelection = (params: UseSegmentSelectionParams) => {
  const context = React.useContext(ServiceContext);

  const [segmentOptions, setSegmentOptions] = useState([]);
  const [subSegmentOptions, setSubSegmentOptions] = useState([]);
  const [segmentFinal, setSegmentFinal] = useState("");
  const [subSegmentFinal, setSubSegmentFinal] = useState("");
  const [segmentDropdownVisible, setSegmentDropdownVisible] = useState(false);
  const [pendingSegmentState, setPendingState] = useState(null);
  const [irisUtterance, setIrisUtterance]= useState(""); // Default value as empty string
  const [appName, setAppName] = useState(""); // Default value as empty string
  const [tileName, setTileName] = useState("");
  // Track whether dropdown was ever shown — prevents useEffect from opening form
  // before dropdown appears (pendingState is set while dropdown is still false initially)
  const dropdownWasShown = React.useRef(false);
  // Store the segment dropdown timer ID so it can be cancelled on panel close/X-button
  const segmentTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup any pending timers on unmount to avoid setState after unmount
  React.useEffect(() => {
    return () => {
      if (segmentTimerRef.current) {
        clearTimeout(segmentTimerRef.current);
        segmentTimerRef.current = null;
      }
    };
  }, []);

  // Track when dropdown becomes visible
  React.useEffect(() => {
    if (segmentDropdownVisible) {
      dropdownWasShown.current = true;
    }
  }, [segmentDropdownVisible]);

  useEffect(() => {
    // Only open form if dropdown was previously shown and is now closed (true→false transition).
    // This prevents premature form rendering before segment dropdown appears.
    if (!segmentDropdownVisible && pendingSegmentState && dropdownWasShown.current) {
      // Guard: if web form is already open (e.g., handleStartButtonFormLogic opened it
      // directly for a saved profile), don't fire openWebFormFinal again — it would
      // call handleChatBotIconClick and create a ghost ticket.
      if (sessionStorage.getItem("WebFormOpen") === "true") {
        setPendingState(null);
        dropdownWasShown.current = false;
        return;
      }
      params.setIsTileHasForm(true);
      openWebFormFinal(
        pendingSegmentState.name,
        pendingSegmentState.iriS_Utterance,
        pendingSegmentState.serviceWithFormKey,
        tileName
      );
      // Clear after use to prevent stale state affecting a later IRIS-only tile
      setPendingState(null);
      dropdownWasShown.current = false;
    }
  }, [pendingSegmentState, segmentDropdownVisible]);

  useEffect(() => {
    fetchDetails();
  }, []);

  const fetchDetails = async () => {
    await Promise.all([getAllSegments(), fetchConfigurableTilesList(context)]);
  };

  const getAllSegments = async () => {
    const data = await fetchSegments(context);
    if (data.length > 0) {
      setSegmentOptions(data.map((item) => ({ key: item.id, text: item.name })));
    } else {
      params.setErrorData(data);
    }
  };

  const getAllSubSegmentFromSegment = async (key: number) => {
    const data = await fetchSubSegments(context, key);
    if (data.length > 0) {
      setSubSegmentOptions(data.map((item) => ({ key: item.id, text: item.name })));
    }
  }

  const passSegmentDataToIris = (segment, subSegment, redirectServiceIdForSMEC = 0) => {
    // Store the selected segments
    setSegmentFinal(segment);
    setSubSegmentFinal(subSegment);
    sessionStorage.setItem("Segment", segment);
    sessionStorage.setItem("SubSegment", subSegment);
    // Mark that save is in progress so hideSegmentDropdown (called by onCancel after this)
    // does not clear pendingState
    segmentSaveInProgress.current = true;
    // Hide segment dropdown
    setSegmentDropdownVisible(false);
    // For P-URL GSOC tiles after segment save
    if (params.isPUrlSegmentTile && params.urlTileData) {
      const tile = params.urlTileData;

      if (tile.isRedirect) {
        // Redirect tiles (e.g., AMR Americas): skip handleChatBotIconClick entirely.
        // The redirect flow handles navigation — bot init here creates ghost ticket.
        params.setIsPUrlSegmentTile(false);
        params.setUrlTileData(null);
        params.purlProcessed.current = true;
      } else {
        // Non-redirect IRIS tiles: initialize bot with segment context
        params.setShowBot(false);
        params.checksIris();
        params.handleChatBotIconClick(
          tile.iriS_Utterance,
          tile.irisAppName,
          tile.tileName,
          segment || "N/A",
          subSegment || "N/A"
        );
        setTimeout(() => {
          params.setShowBot(true);
        }, 300);
        params.setIsPUrlSegmentTile(false);
        params.setUrlTileData(null);
        params.purlProcessed.current = true;
      }

    } else if (pendingSegmentState) {
      // Don't call openWebFormFinal directly here — the closure still has
      // segmentDropdownVisible=true (stale). Let the useEffect handle it on next render
      // when React updates segmentDropdownVisible=false and pendingSegmentState still exists.
    } else {
      // Guard: if web form is already open, don't trigger IRIS bot (prevents ghost ticket)
      if (sessionStorage.getItem("WebFormOpen") === "true") {
        return;
      }
      // Regular flow (non-P-URL) - proceed with Iris
      params.setShowBot(false);
      params.checksIris();
      let filteredService = [];
      if (redirectServiceIdForSMEC > 0) {
        filteredService = params.dataServiceWorkspace.filter((srv) => srv.serviceID === redirectServiceIdForSMEC);
      }

      // Use filtered service properties if available, otherwise use default values
      const service = filteredService[0];
      const finalIrisUtterance = service?.iriS_Utterance ?? irisUtterance;
      const finalTileName = service?.tileName ?? tileName;
      params.handleChatBotIconClick(
        finalIrisUtterance,
        appName,
        finalTileName,
        segment,
        subSegment
      );

      // Show bot for regular flow too
      setTimeout(() => {
        params.setShowBot(true);
      }, 300);
    }
  };

  const openPanelForSMEC = async (id, segmentKey: any, subsegmentkey: any, isRecommended = false, isStartClick = false, isReloadWorkSpace = false, shouldSaveTileProfile = false) => {
    // WI-1249082: If target tile has copilot access with enableSelfHelp=true, don't open form panel — agent-only mode
    const targetCard = params.dataServiceWorkspace.find((srv) => srv.serviceID === id);
    const targetKey = (targetCard?.tileName || '').toLowerCase();
    if (params.copilotAccessMap[targetKey]?.hasAccess === true && params.copilotAccessMap[targetKey]?.enableSelfHelp === true) {
      return;
    }

    //The state is used to identify that user is redirected from another form. Eg: used has opened the form "Account Management Requests (Americas Enterprise only)" then selected the segment 
    // then redirected to the form "Account Management Requests" So with this state we assumes that user has selected the segment i.e firstSelectedOption state in V2_DynamicDropdown.tsx.
    params.setIsRedirectFromAnotherForm(true);

    const workspaceData = isReloadWorkSpace ? (await params.fetchUserWorkspacesAndDefaultTiles().then(res => params.validateResponse(res.response1, res.response2, res.result1, res.result2))).jsonDataWorkspace : params.dataServiceWorkspace;
    isReloadWorkSpace && params.setDataServiceWorkspace(workspaceData);

    // Use common helper function for event logging
    const card = params.handlePanelOpening(id, workspaceData, isRecommended, isStartClick);
    // Segment is always required. Sub-segment is optional: the new segment model
    // (e.g. "SME&C - Corporate", "SME&C - SMB", "MS Elevate") encodes the sub-segment
    // in the segment value itself, so those tiles have no sub-segment. Gating on
    // subsegmentkey here would skip setTileProfileResponse for those tiles, leaving the
    // redirect target's profile null and hiding the "Edit Segment" tab.
    if (segmentKey) {
      if (shouldSaveTileProfile) {
        // SMEC Redirect Tile Profile Handling:
        // When user starts "Account Management Requests (Americas Enterprise only)" and selects
        // an SME&C segment, they get redirected to "Account Management Requests".
        // Since this is the first time for the redirect target tile, setTileProfileResponse is null,
        // so we manually set it with the selected segment/subsegment values to maintain user preferences
        // and ensure proper tile profile state for future interactions. Sub-segment persisted as 0 when absent.
        const profileJson = {
          "isSuccess": true, "profileJson": { "segments": Number(segmentKey), "subSegments": subsegmentkey ? Number(subsegmentkey) : 0 }
        };
        params.setTileProfileResponse(profileJson);
      }
      params.setIsDropdownModalVisible(false);
    }

    params.checkTileFormStatus(card);
    // Ghost ticket fix: Do NOT call checkIrisService during SME&C redirect.
    // checkIrisService triggers handleChatBotIconClick → setChatBotClicked(true) → IRIS bot
    // initializes with the redirect tile's utterance and auto-creates a ghost ticket.
    // The bot is already running from the original Americas tile — only the form panel needs to open.
    params.SetIsopenSegment(false);

    // Retrieve and set, then delete from session storage
    const selectedSegmentKey = sessionStorage.getItem("firstSelectedOptionAm") || "";
    params.setUserSelectedOption(selectedSegmentKey);
    // Map 'smec' to 'SME&C' for display, then find the corresponding key in segmentOptions
    if (selectedSegmentKey) {
      const displaySegmentName = selectedSegmentKey === "smec" ? "SME&C" : selectedSegmentKey;  //in future remove this is smec naming issue is resolved.
      const segmentId = segmentOptions?.find(option => option.text === displaySegmentName)?.key;;
      if (segmentId) {
        await getAllSubSegmentFromSegment(segmentId);
      }
    }
    sessionStorage.removeItem("firstSelectedOptionAm");
    params.setOpenPanelId(id);
  }

  // Track whether passSegmentDataToIris was called (save path) to distinguish
  // from a true cancel when hideSegmentDropdown is subsequently called.
  const segmentSaveInProgress = React.useRef(false);

  // Called on cancel — clears pending state so form doesn't open
  const hideSegmentDropdown = () => {
    // Cancel the pending 2-second timer to prevent ghost rendering after X-button close
    if (segmentTimerRef.current) {
      clearTimeout(segmentTimerRef.current);
      segmentTimerRef.current = null;
    }
    if (!segmentSaveInProgress.current) {
      setPendingState(null);
      dropdownWasShown.current = false;
    }
    segmentSaveInProgress.current = false;
    setSegmentDropdownVisible(false);
    if (params.isPUrlSegmentTile) {
      params.setInfoPanelZIndex("9999910"); // Restore original z-index
      // Clear P-URL segment flags so that if this tile redirects (e.g., SME&C → target tile),
      // the redirect tile's save won't hit the P-URL GSOC branch in passSegmentDataToIris.
      // For the normal P-URL save path, passSegmentDataToIris already clears these flags
      // before hideSegmentDropdown runs (called via onCancel in handleSegmentSubSegmentSave).
      params.setIsPUrlSegmentTile(false);
      params.setUrlTileData(null);
    }
  };

  // Called after save actions (e.g., handleSaveAmericas) — preserves pending state
  // so the useEffect can open the form
  const hideSegmentDropdownAfterSave = () => {
    segmentSaveInProgress.current = true;
    hideSegmentDropdown();
  };

  const resetRedirectState = () => {
    params.setIsRedirectFromAnotherForm(false);
    params.setUserSelectedOption("");
    params.SetIsopenSegment(true); // Ensure segment dropdown is visible for normal services
  };

  const handleFormAndSegment = (
    isTileConsistsWebform,
    name,
    iriS_Utterance,
    serviceWithFormKey,
    tileName
  ) => {
    // Clear any previous redirect state when opening a normal service
    resetRedirectState();

    params.setDialogTitle(name);
    setIrisUtterance(iriS_Utterance);
    setAppName(params.irisAppName);
    setTileName(tileName);

    // delay segment popup by 2000ms — store timer ID so it can be cancelled
    if (segmentTimerRef.current) {
      clearTimeout(segmentTimerRef.current);
    }
    segmentTimerRef.current = setTimeout(() => {
      segmentTimerRef.current = null;
      setSegmentDropdownVisible(true);
    }, 2000);

    // Only set pending state for tiles with webform.
    // For IRIS-only tiles (no form), passSegmentDataToIris's else branch
    // will handle opening the Iris bot after segment selection.
    if (isTileConsistsWebform) {
      setPendingState({ name, iriS_Utterance, serviceWithFormKey });
    } else {
      // Clear any stale pending state from a previous form tile
      setPendingState(null);
    }
  };

  const openWebFormFinal = (
    name,
    iriS_Utterance,
    serviceWithFormKey,
    tileName
  ) => {
    if (!segmentDropdownVisible) {
      params.openWebForm(name, iriS_Utterance, tileName, serviceWithFormKey);
      sessionStorage.setItem("WebFormOpen", "true");
      disableDWRIris(tileName);
    }
  };

  const disableDWRIris = (tileName: any) => {
    const DisableIRIS_DWR_CONST = "DWR (daily work report)";
    if (tileName === DisableIRIS_DWR_CONST) {
      setTimeout(() => {
        const irisHeaderElement = document.querySelector("._3bARu");
        // Check if the element exists and get its parent
        if (irisHeaderElement) {
          const parentDiv = irisHeaderElement.parentElement;
          if (parentDiv) {
            parentDiv.style.display = "none";
          }
        }
      }, 3000);
    }
  };

  return {
    segmentOptions, setSegmentOptions,
    subSegmentOptions, setSubSegmentOptions,
    segmentFinal, setSegmentFinal,
    subSegmentFinal, setSubSegmentFinal,
    segmentDropdownVisible, setSegmentDropdownVisible,
    pendingSegmentState, setPendingState,
    irisUtterance, setIrisUtterance,
    appName, setAppName,
    tileName, setTileName,
    getAllSegments,
    getAllSubSegmentFromSegment,
    passSegmentDataToIris,
    openPanelForSMEC,
    fetchDetails,
    hideSegmentDropdown,
    hideSegmentDropdownAfterSave,
    resetRedirectState,
    handleFormAndSegment,
    openWebFormFinal,
  };
};

// Generated by Copilot
// updated on: 2026-07-07 | 8 lines added/updated | [Auto Generated] | FYRO: openPanelForSMEC persists redirect-target tile profile for segment-only tiles (gate on segmentKey only; subSegments 0 when absent) so the Edit Segment tab appears | Time Taken 4 min
